/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  FileUp,
  XCircle,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMattersBulkCreateMutation } from '@/services/api/bulkMattersService';
import { useGetFirmsQuery } from '@/services/api/firmsService';
import {
  BulkMatterCreateResponseSchema,
  BulkMatterResultItemSchema,
  BulkMatterRowSchema,
  MatterStatus,
} from '@/services/api/generated/bulkMattersService';

interface BulkMatterCreateModalProps {
  open: boolean;
  onClose: () => void;
}

type Step = 'upload' | 'preview' | 'result';

// All available matter statuses
const MATTER_STATUSES: MatterStatus[] = [
  'Initial Review',
  'Case Setup',
  'Treatment',
  'Demand Letter',
  'Treatment Complete',
  'Ready for Demand',
  'Demand Preparation',
  'Complete',
  'Hidden',
  'In Progress',
  'Client Dropped',
  'To Be Dropped',
];

export const BulkMatterCreateModal = ({
  open,
  onClose,
}: BulkMatterCreateModalProps) => {
  const [step, setStep] = useState<Step>('upload');
  const [selectedFirmId, setSelectedFirmId] = useState<string>('');
  const [defaultStatus] = useState<MatterStatus>('Initial Review');
  const [matters, setMatters] = useState<BulkMatterRowSchema[]>([]);
  const [showStatusList, setShowStatusList] = useState(false);
  const [previewResult, setPreviewResult] =
    useState<BulkMatterCreateResponseSchema | null>(null);
  const [finalResult, setFinalResult] =
    useState<BulkMatterCreateResponseSchema | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: firms } = useGetFirmsQuery({});
  const [bulkCreate, { isLoading }] = useMattersBulkCreateMutation();

  // Helper to normalize status values from CSV to MatterStatus enum
  const normalizeStatus = (status: string): MatterStatus | null => {
    const normalized = status.trim().toLowerCase();
    const statusMap: Record<string, MatterStatus> = {
      'initial review': 'Initial Review',
      intake: 'Initial Review', // Legacy: map to Initial Review
      'case setup': 'Case Setup',
      treatment: 'Treatment',
      'demand letter': 'Demand Letter',
      demand: 'Demand Letter',
      'new lead': 'Initial Review', // Legacy: map to Initial Review
      'treatment complete': 'Treatment Complete',
      'ready for demand': 'Ready for Demand',
      'demand preparation': 'Demand Preparation',
      complete: 'Complete',
      hidden: 'Hidden',
      'in progress': 'In Progress',
      'client dropped': 'Client Dropped',
      'to be dropped': 'To Be Dropped',
    };
    return statusMap[normalized] || null;
  };

  const parseCSV = useCallback(
    (content: string): BulkMatterRowSchema[] => {
      const lines = content.trim().split('\n');
      if (lines.length < 2) {
        throw new Error('CSV must have at least a header row and one data row');
      }

      const header = lines[0]
        .toLowerCase()
        .split(',')
        .map((h) => h.trim());
      const nameIndex = header.indexOf('matter_name');
      const urlIndex = header.indexOf('external_cms_url');
      const statusIndex = header.indexOf('status');

      if (nameIndex === -1 || urlIndex === -1) {
        throw new Error(
          'CSV must have columns: matter_name, external_cms_url. Optional: status',
        );
      }

      const parsedMatters: BulkMatterRowSchema[] = [];
      for (let i = 1; i < lines.length; i += 1) {
        const line = lines[i].trim();
        if (line) {
          const values = line.split(',').map((v) => v.trim());
          if (values.length < Math.max(nameIndex, urlIndex) + 1) {
            throw new Error(`Line ${i + 1}: Invalid CSV format`);
          }

          // Get status from CSV if present, otherwise use default
          let status: MatterStatus = defaultStatus;
          if (statusIndex !== -1 && values[statusIndex]) {
            const normalized = normalizeStatus(values[statusIndex]);
            if (normalized) {
              status = normalized;
            }
          }

          parsedMatters.push({
            matter_name: values[nameIndex],
            external_cms_url: values[urlIndex],
            status,
          });
        }
      }

      return parsedMatters;
    },
    [defaultStatus],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const parsedMatters = parseCSV(content);
          setMatters(parsedMatters);
          setError(null);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to parse CSV');
          setMatters([]);
        }
      };
      reader.readAsText(file);
    },
    [parseCSV],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  });

  const handlePreview = async () => {
    if (!selectedFirmId || matters.length === 0) return;

    try {
      const result = await bulkCreate({
        bulkMatterCreateRequestSchema: {
          firm_id: selectedFirmId,
          matters,
          dry_run: true,
        },
      }).unwrap();

      setPreviewResult(result);
      setStep('preview');
      setError(null);
    } catch (err) {
      setError('Failed to preview matters. Please try again.');
    }
  };

  const handleConfirm = async () => {
    if (!selectedFirmId || matters.length === 0) return;

    try {
      const result = await bulkCreate({
        bulkMatterCreateRequestSchema: {
          firm_id: selectedFirmId,
          matters,
          dry_run: false,
        },
      }).unwrap();

      setFinalResult(result);
      setStep('result');
      setError(null);
    } catch (err) {
      setError('Failed to create matters. Please try again.');
    }
  };

  const handleClose = () => {
    setStep('upload');
    setSelectedFirmId('');
    setMatters([]);
    setPreviewResult(null);
    setFinalResult(null);
    setError(null);
    onClose();
  };

  const handleCopyStatus = async (status: string) => {
    try {
      await navigator.clipboard.writeText(status);
      toast.success(`Copied "${status}" to clipboard`);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const renderResultItem = (
    item: BulkMatterResultItemSchema,
    isPreview: boolean,
  ) => {
    const statusIcons: Record<string, React.ReactNode> = {
      created: <CheckCircle2 className="h-4 w-4 text-green-600" />,
      duplicate: <AlertCircle className="h-4 w-4 text-yellow-600" />,
      error: <XCircle className="h-4 w-4 text-red-600" />,
    };

    const getStatusLabel = (status: string) => {
      if (isPreview && status === 'created') {
        return 'Ready to create';
      }
      return status.charAt(0).toUpperCase() + status.slice(1);
    };

    return (
      <TableRow key={`${item.external_cms_url}-${item.status}`}>
        <TableCell className="flex items-center gap-2">
          {statusIcons[item.status]}
          <span>{getStatusLabel(item.status)}</span>
        </TableCell>
        <TableCell>{item.matter_name}</TableCell>
        <TableCell className="text-sm text-gray-500">
          {item.external_cms_url}
        </TableCell>
        {item.error && (
          <TableCell className="text-sm text-red-600">{item.error}</TableCell>
        )}
      </TableRow>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Create Matters</DialogTitle>
          <DialogDescription>
            Upload a CSV file with columns: matter_name, external_cms_url,
            status (optional)
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {step === 'upload' && (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="firm">Select Firm</Label>
              <Select value={selectedFirmId} onValueChange={setSelectedFirmId}>
                <SelectTrigger id="firm">
                  <SelectValue placeholder="Select a firm" />
                </SelectTrigger>
                <SelectContent>
                  {firms?.map((firm) => (
                    <SelectItem key={firm.id} value={firm.id}>
                      {firm.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Default Status</Label>
              <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm">
                Initial Review
              </div>
              <p className="text-xs text-gray-500">
                All new matters will be created with Initial Review status
              </p>
            </div>

            <div className="border rounded-lg p-3 bg-gray-50">
              <button
                type="button"
                onClick={() => setShowStatusList(!showStatusList)}
                className="flex items-center justify-between w-full text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <span>Valid CSV Status Values</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${
                    showStatusList ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {showStatusList && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-600 mb-2">
                    Click to copy any of these values for your CSV
                    (case-insensitive):
                  </p>
                  <ul className="space-y-1 text-xs font-mono bg-white p-3 rounded border border-gray-200">
                    <li
                      className="cursor-pointer hover:bg-gray-50 p-1 rounded"
                      onClick={() => handleCopyStatus('initial review')}
                    >
                      initial review
                    </li>
                    <li
                      className="cursor-pointer hover:bg-gray-50 p-1 rounded"
                      onClick={() => handleCopyStatus('intake')}
                    >
                      intake
                    </li>
                    <li
                      className="cursor-pointer hover:bg-gray-50 p-1 rounded"
                      onClick={() => handleCopyStatus('case setup')}
                    >
                      case setup
                    </li>
                    <li
                      className="cursor-pointer hover:bg-gray-50 p-1 rounded"
                      onClick={() => handleCopyStatus('treatment')}
                    >
                      treatment
                    </li>
                    <li
                      className="cursor-pointer hover:bg-gray-50 p-1 rounded"
                      onClick={() => handleCopyStatus('demand letter')}
                    >
                      demand letter
                    </li>
                    <li
                      className="cursor-pointer hover:bg-gray-50 p-1 rounded"
                      onClick={() => handleCopyStatus('new lead')}
                    >
                      new lead
                    </li>
                    <li
                      className="cursor-pointer hover:bg-gray-50 p-1 rounded"
                      onClick={() => handleCopyStatus('treatment complete')}
                    >
                      treatment complete
                    </li>
                    <li
                      className="cursor-pointer hover:bg-gray-50 p-1 rounded"
                      onClick={() => handleCopyStatus('ready for demand')}
                    >
                      ready for demand
                    </li>
                    <li
                      className="cursor-pointer hover:bg-gray-50 p-1 rounded"
                      onClick={() => handleCopyStatus('demand preparation')}
                    >
                      demand preparation
                    </li>
                    <li
                      className="cursor-pointer hover:bg-gray-50 p-1 rounded"
                      onClick={() => handleCopyStatus('complete')}
                    >
                      complete
                    </li>
                    <li
                      className="cursor-pointer hover:bg-gray-50 p-1 rounded"
                      onClick={() => handleCopyStatus('hidden')}
                    >
                      hidden
                    </li>
                    <li
                      className="cursor-pointer hover:bg-gray-50 p-1 rounded"
                      onClick={() => handleCopyStatus('in progress')}
                    >
                      in progress
                    </li>
                    <li
                      className="cursor-pointer hover:bg-gray-50 p-1 rounded"
                      onClick={() => handleCopyStatus('client dropped')}
                    >
                      client dropped
                    </li>
                    <li
                      className="cursor-pointer hover:bg-gray-50 p-1 rounded"
                      onClick={() => handleCopyStatus('to be dropped')}
                    >
                      to be dropped
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input {...getInputProps()} />
              <FileUp className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-4 text-sm text-gray-600">
                {isDragActive
                  ? 'Drop the CSV file here'
                  : 'Drag and drop a CSV file, or click to select'}
              </p>
              <p className="mt-2 text-xs text-gray-500">
                CSV must have columns: matter_name, external_cms_url, status
                (optional)
              </p>
            </div>

            {matters.length > 0 && (
              <div className="space-y-2">
                <Label>Parsed Matters ({matters.length})</Label>
                <div className="border rounded-lg max-h-60 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Matter Name</TableHead>
                        <TableHead>External CMS URL</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {matters.slice(0, 10).map((matter, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{matter.matter_name}</TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {matter.external_cms_url}
                          </TableCell>
                          <TableCell className="text-sm">
                            {matter.status}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {matters.length > 10 && (
                    <p className="p-2 text-sm text-center text-gray-500">
                      ...and {matters.length - 10} more
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'preview' && previewResult && (
          <div className="space-y-4">
            <Alert>
              <AlertDescription>
                This is a preview. Review the results and click &quot;Confirm
                &amp; Create&quot; to actually create the matters.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-700">
                  {previewResult.summary.created || 0}
                </p>
                <p className="text-sm text-green-600">Will be created</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="text-2xl font-bold text-yellow-700">
                  {previewResult.summary.duplicates || 0}
                </p>
                <p className="text-sm text-yellow-600">Duplicates</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-700">
                  {previewResult.summary.errors || 0}
                </p>
                <p className="text-sm text-red-600">Errors</p>
              </div>
            </div>

            <div className="border rounded-lg max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Matter Name</TableHead>
                    <TableHead>External CMS URL</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewResult.created?.map((item) =>
                    renderResultItem(item, true),
                  )}
                  {previewResult.duplicates?.map((item) =>
                    renderResultItem(item, true),
                  )}
                  {previewResult.errors?.map((item) =>
                    renderResultItem(item, true),
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {step === 'result' && finalResult && (
          <div className="space-y-4">
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Successfully processed {finalResult.summary.total} matters!
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-700">
                  {finalResult.summary.created || 0}
                </p>
                <p className="text-sm text-green-600">Created</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="text-2xl font-bold text-yellow-700">
                  {finalResult.summary.duplicates || 0}
                </p>
                <p className="text-sm text-yellow-600">Duplicates (skipped)</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-700">
                  {finalResult.summary.errors || 0}
                </p>
                <p className="text-sm text-red-600">Errors</p>
              </div>
            </div>

            <div className="border rounded-lg max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Matter Name</TableHead>
                    <TableHead>External CMS URL</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {finalResult.created?.map((item) =>
                    renderResultItem(item, false),
                  )}
                  {finalResult.duplicates?.map((item) =>
                    renderResultItem(item, false),
                  )}
                  {finalResult.errors?.map((item) =>
                    renderResultItem(item, false),
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'upload' && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handlePreview}
                disabled={!selectedFirmId || matters.length === 0 || isLoading}
              >
                Preview
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('upload')}>
                Back
              </Button>
              <Button onClick={handleConfirm} disabled={isLoading}>
                Confirm & Create
              </Button>
            </>
          )}
          {step === 'result' && <Button onClick={handleClose}>Close</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
